import { useRouter } from 'next/router';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { DozukiHeaderProps } from '@dozuki/web-js/components';

type ActiveNavItem = DozukiHeaderProps['activeItem'];

type ActiveNavItemContextValue = {
   activeNavItem?: ActiveNavItem;
   setActiveNavItem: (activeNavItem: ActiveNavItem) => void;
};

const ActiveNavItemContext = createContext<ActiveNavItemContextValue>({
   setActiveNavItem: () => null,
});

export function ActiveNavItemContextProvider({ children }: { children: ReactNode }) {
   const [activeNavItem, setActiveNavItem] = useState<ActiveNavItem>();
   const router = useRouter();

   useEffect(() => {
      // On route change, disable the underline in the nav bar
      function handleRouteChange() {
         setActiveNavItem(undefined);
      }

      router.events.on('routeChangeStart', handleRouteChange);

      // On unmount, remove our event listener
      return () => router.events.off('routeChangeComplete', handleRouteChange);
   }, []);

   return (
      <ActiveNavItemContext.Provider value={{ activeNavItem, setActiveNavItem }}>
         {children}
      </ActiveNavItemContext.Provider>
   );
}

export function useActiveNavItem() {
   const { activeNavItem } = useContext(ActiveNavItemContext);
   return activeNavItem;
}

export function ActiveNavItem({ value }: { value: ActiveNavItem }) {
   const activeNavItemContext = useContext(ActiveNavItemContext);

   useEffect(() => {
      if (activeNavItemContext.activeNavItem !== value) {
         activeNavItemContext.setActiveNavItem(value);
      }
   }, [value]);

   return null;
}
